import { formatPrice } from '../utils/formatPrice';

/**
 * Receipt printer service using ReceiptPrinterEncoder + WebUSBReceiptPrinter.
 * Falls back to browser print with thermal-sized CSS layout.
 */

let printerInstance = null;
let encoderClass = null;

/**
 * Check if WebUSB is available in the current browser.
 */
export function isWebUSBAvailable() {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

/**
 * Connect to a receipt printer via WebUSB.
 * Returns the printer instance or null.
 */
export async function connectPrinter() {
  if (!isWebUSBAvailable()) {
    throw new Error('WebUSB is not supported in this browser. Use Chrome or Edge.');
  }

  try {
    const { default: WebUSBReceiptPrinter } = await import(
      'web-usb-receipt-printer'
    );
    const { default: ReceiptPrinterEncoder } = await import(
      'receipt-printer-encoder'
    );

    encoderClass = ReceiptPrinterEncoder;
    printerInstance = new WebUSBReceiptPrinter();
    await printerInstance.connect();
    return printerInstance;
  } catch (err) {
    printerInstance = null;
    throw err;
  }
}

/**
 * Disconnect the current printer.
 */
export async function disconnectPrinter() {
  if (printerInstance) {
    try {
      await printerInstance.disconnect();
    } catch (e) {
      // Already disconnected
    }
    printerInstance = null;
  }
}

/**
 * Get the current printer connection status.
 */
export function getPrinterStatus() {
  return {
    connected: !!printerInstance,
    webUSBAvailable: isWebUSBAvailable(),
  };
}

/**
 * Print a receipt for an order.
 * Uses WebUSB if connected, otherwise falls back to browser print.
 */
export async function printReceipt(order, shopInfo, settings) {
  const receiptSettings = {
    widthMm: settings?.receipt_width_mm || 80,
    charsPerLine: settings?.receipt_chars_per_line || 48,
  };
  if (printerInstance && encoderClass) {
    return printViaWebUSB(order, shopInfo, receiptSettings);
  }
  return printViaBrowser(order, shopInfo, receiptSettings);
}

/**
 * Print via WebUSB-connected thermal printer.
 */
async function printViaWebUSB(order, shopInfo, receiptSettings) {
  const encoder = new encoderClass({
    width: receiptSettings.charsPerLine,
    wordWrap: true,
  });

  const shop = shopInfo || {};

  encoder
    .initialize()
    .align('center')
    .bold(true)
    .text(shop.name || 'Shop')
    .bold(false)
    .newline();

  if (shop.address || shop.city) {
    const addr = [shop.address, shop.city, shop.state, shop.postcode]
      .filter(Boolean)
      .join(', ');
    encoder.text(addr).newline();
  }

  const divider = '-'.repeat(receiptSettings.charsPerLine);

  encoder
    .text(divider)
    .newline()
    .align('left')
    .text(`Order: #${order.number}`)
    .newline()
    .text(`Date: ${formatReceiptDate(order.date_created)}`)
    .newline();

  if (order.customer && order.customer.name) {
    encoder.text(`Customer: ${order.customer.name}`).newline();
  }

  encoder.text(divider).newline();

  // Items
  for (const item of order.items || []) {
    const qty = `${item.quantity}x`;
    const price = formatPrice(item.total);
    const name =
      item.name.length > 30 ? item.name.substring(0, 30) + '...' : item.name;

    encoder.text(`${qty} ${name}`).newline();
    encoder.align('right').text(price).align('left').newline();
  }

  encoder.text(divider).newline();

  // Totals
  encoder.text(padLine('Subtotal:', formatPrice(order.subtotal), receiptSettings.charsPerLine)).newline();

  if (order.discount_total > 0) {
    encoder
      .text(padLine('Discount:', `-${formatPrice(order.discount_total)}`, receiptSettings.charsPerLine))
      .newline();
  }

  if (order.total_tax > 0) {
    encoder.text(padLine('Tax:', formatPrice(order.total_tax), receiptSettings.charsPerLine)).newline();
  }

  encoder
    .bold(true)
    .text(padLine('TOTAL:', formatPrice(order.total), receiptSettings.charsPerLine))
    .bold(false)
    .newline()
    .text(divider)
    .newline()
    .text(
      padLine(
        'Payment:',
        order.payment_title || order.payment_method,
        receiptSettings.charsPerLine
      )
    )
    .newline()
    .newline()
    .align('center')
    .text('Thank you for your purchase!')
    .newline()
    .newline()
    .newline()
    .cut();

  const data = encoder.encode();
  await printerInstance.print(data);
}

/**
 * Fallback: open a thermal-sized print view in the browser.
 * Uses a Blob URL so the full HTML is loaded reliably.
 */
function printViaBrowser(order, shopInfo, receiptSettings) {
  const shop = shopInfo || {};

  const address = [shop.address, shop.city, shop.state, shop.postcode]
    .filter(Boolean)
    .join(', ');

  let itemsHtml = '';
  for (const item of order.items || []) {
    itemsHtml += `<div class="item-name">${esc(item.quantity + 'x ' + item.name)}</div>`;
    itemsHtml += `<div class="row"><span></span><span>${esc(formatPrice(item.total))}</span></div>`;
  }

  let totalsHtml = `<div class="row"><span>Subtotal:</span><span>${esc(formatPrice(order.subtotal))}</span></div>`;
  if (order.discount_total > 0) {
    totalsHtml += `<div class="row"><span>Discount:</span><span>-${esc(formatPrice(order.discount_total))}</span></div>`;
  }
  if (order.total_tax > 0) {
    totalsHtml += `<div class="row"><span>Tax:</span><span>${esc(formatPrice(order.total_tax))}</span></div>`;
  }

  const customerLine = order.customer && order.customer.name
    ? `<div>Customer: ${esc(order.customer.name)}</div>`
    : '';

  const widthMm = receiptSettings.widthMm;

  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<title>Receipt #${esc(String(order.number))}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: ${widthMm}mm; margin: 0 auto; padding: 4mm; color: #000; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; }
  .item-name { margin-bottom: 2px; }
  .total-row { font-weight: bold; font-size: 14px; margin-top: 4px; }
  .thanks { text-align: center; margin-top: 12px; }
  @media print { @page { margin: 0; size: ${widthMm}mm auto; } body { width: ${widthMm}mm; } }
</style>
</head><body>
  <div class="center bold">${esc(shop.name || 'Shop')}</div>
  ${address ? `<div class="center">${esc(address)}</div>` : ''}
  <div class="line"></div>
  <div>Order: #${esc(String(order.number))}</div>
  <div>Date: ${esc(formatReceiptDate(order.date_created))}</div>
  ${customerLine}
  <div class="line"></div>
  ${itemsHtml}
  <div class="line"></div>
  ${totalsHtml}
  <div class="row total-row"><span>TOTAL:</span><span>${esc(formatPrice(order.total))}</span></div>
  <div class="line"></div>
  <div class="row"><span>Payment:</span><span>${esc(order.payment_title || order.payment_method || '')}</span></div>
  <div class="thanks">Thank you for your purchase!</div>
  <script>window.onload = function() { window.focus(); window.print(); }<\/script>
</body></html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank', 'width=320,height=600');

  // Clean up the blob URL after a delay
  if (printWindow) {
    setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
  }
}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function padLine(label, value, width = 48) {
  const spaces = width - label.length - value.length;
  return label + ' '.repeat(Math.max(1, spaces)) + value;
}

function formatReceiptDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
